import { auth, functions, httpsCallable } from './firebaseConfig'; // Import the app from firebaseConfig.ts
import { applyActionCode, createUserWithEmailAndPassword, EmailAuthProvider, reauthenticateWithCredential, sendPasswordResetEmail, onAuthStateChanged, signInAnonymously, signInWithEmailAndPassword, signOut, updatePassword, User, UserCredential, verifyBeforeUpdateEmail } from "firebase/auth";
import { useRouter } from "next/navigation";
import React from "react";
import { sendEmailVerification, updateProfile } from "@firebase/auth";
import { ensureUserProfile } from "@/lib/UserProfileEnsure";
import { persistSignupProfileMetadata } from "@/lib/persistSignupProfileMetadata";
import { logAnalyticsEvent } from "@/firebase/firebaseAnalytics";
import { readPublicVideoClaimContext } from "@/lib/PublicVideoClaim";
import { trackToltReferralSignup } from "@/lib/Tolt";
import { getSafeReturnToFromLocation, persistReturnTo } from "@/lib/AuthReturnTo";
import { getAppLocale } from "@/lib/appLocale";
import { readVerificationSendReason, signInFailureDisplayMessage, signUpFailureDisplayMessage } from "@/lib/signupVerification";
export type SignupPending = {
  profileReady: boolean;
  verificationEmailSent: boolean;
  /** Resolves after the verification email attempt. The signup screen does not wait on it. */
  verificationEmail?: Promise<boolean>;
  /**
   * emailVerified as reported by ensureUserProfile, which verifies allowlisted
   * addresses before returning. Undefined when that call failed.
   */
  serverEmailVerified?: boolean;
};
type AuthEmailProvider = "resend" | "firebase";

/**
 * Continue URLs for Auth action links must be Braiv https hosts (validated
 * server-side). Map localhost / alternate origins to the env Braiv host.
 */
function authContinueOrigin(): string {
  if (typeof window === "undefined") {
    return "https://app.braiv.co";
  }
  const origin = window.location.origin;
  if (/^https:\/\/(dev|preview|app)\.braiv\.co$/i.test(origin)) {
    return origin;
  }
  const projectId = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || "";
  if (projectId.includes("dev")) {
    return "https://dev.braiv.co";
  }
  if (projectId.includes("preview")) {
    return "https://preview.braiv.co";
  }
  return "https://app.braiv.co";
}
function authEmailLocale(): string {
  return getAppLocale();
}

/**
 * Get the current user
 */
export const currentUser = function (): User | null {
  if (typeof window === "undefined") {
    return null;
  }
  return auth.currentUser;
};
export const ensureAnonymousUser = async function (): Promise<User> {
  const existingUser = currentUser();
  if (existingUser) {
    return existingUser;
  }
  const hydratedUser = await new Promise<User | null>(resolve => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      unsubscribe();
      resolve(user);
    }, () => {
      unsubscribe();
      resolve(auth.currentUser);
    });
  });
  if (hydratedUser) {
    return hydratedUser;
  }
  const credential = await signInAnonymously(auth);
  return credential.user;
};

/**
 * Sign up a user with email and password
 * @param email
 * @param password
 * @param displayName
 */
export const signUp = async function ({
  email,
  password,
  displayName,
  deferVerificationEmail = false
}: {
  email: string;
  password: string;
  displayName: string;
  /**
   * When true, the caller waits for a post-create reload before sending mail.
   * Allowlisted signups are verified by Auth onCreate and must not be asked
   * to open a link.
   */
  deferVerificationEmail?: boolean;
}): Promise<{
  credential: UserCredential | null;
  error: any;
  displayMessage?: string;
  pending?: SignupPending;
}> {
  let credential: UserCredential | null = null;
  try {
    credential = await createUserWithEmailAndPassword(auth, email, password);
  } catch (e: any) {
    const displayMessage = signUpFailureDisplayMessage(e?.code, e?.message || "Unable to complete signup.");
    return {
      credential: null,
      error: e,
      displayMessage
    };
  }
  const pending: SignupPending = {
    profileReady: true,
    verificationEmailSent: true
  };
  try {
    await updateProfile(credential.user, {
      displayName
    });
  } catch (profileError) {
    console.error("Signup display name update failed", profileError);
  }
  try {
    const ensured = await ensureUserProfile({
      uid: credential.user.uid,
      providerType: "password",
      source: "sign_up"
    });
    pending.serverEmailVerified = ensured.emailVerified;
  } catch (ensureError) {
    pending.profileReady = false;
    console.error("Signup profile ensure failed", ensureError);
  }
  void persistSignupProfileMetadata(credential.user.uid).catch(metadataError => {
    console.error("Signup profile metadata failed", metadataError);
  });
  const verificationEmail = deferVerificationEmail ? undefined : sendSignupVerificationEmail(email);
  return {
    credential,
    error: null,
    pending: {
      ...pending,
      verificationEmail
    }
  };
};

/**
 * Read `emailVerified` from the Auth user record, then force-refresh the ID
 * token so `email_verified` matches. The signup response and the first token
 * are minted before the allowlist onCreate update.
 */
export async function reloadEmailVerification(user: User): Promise<boolean> {
  try {
    if (!user.emailVerified) {
      await user.reload();
    }
  } catch (error) {
    console.error("Email verification reload failed", error);
    return false;
  }
  if (!user.emailVerified) {
    return false;
  }
  try {
    await user.getIdToken(true);
  } catch (error) {
    console.error("Email verification token refresh failed", error);
  }
  return user.emailVerified === true;
}

/**
 * Sign in a user with email and password
 * @param email
 * @param password
 */
export const signIn = async function (email: string, password: string): Promise<{
  credential: UserCredential | null;
  error: any;
  displayMessage?: string;
}> {
  let credential: UserCredential | null = null,
    error: any = null;
  return signInWithEmailAndPassword(auth, email, password).then(cred => {
    credential = cred;
    return {
      credential,
      error
    };
  }).catch(e => {
    const displayMessage = signInFailureDisplayMessage(e?.code, e?.message || "");
    error = e;
    return {
      credential,
      error,
      displayMessage
    };
  });
};
export const changePassword = async function (currentPassword: string, newPassword: string) {
  const user = currentUser();
  console.log('Changing password for user', user?.email, currentPassword, newPassword);
  if (user == null) {
    return Promise.reject(new Error('No user is signed in.'));
  }
  const credential = EmailAuthProvider.credential(user.email || '', currentPassword);
  return reauthenticateWithCredential(user, credential).then(userCredential => {
    console.log('Re-authenticated user', userCredential);
    return updatePassword(user, newPassword);
  }).catch(error => {
    console.log('Error re-authenticating user', error.code, error.message);
    const code = error.code;
    let message = error.message;
    if (code === 'auth/invalid-credential') {
      message = "The current password is incorrect.";
    } else if (code === 'auth/weak-password') {
      message = "The password should be at least 6 characters.";
    } else if (code === 'auth/user-disabled') {
      message = "The user corresponding to the given email has been disabled.";
    } else if (code === 'auth/wrong-password') {
      message = "The current password is incorrect.";
    }
    throw new Error(message);
  });
};
export const changeEmail = async function (currentPassword: string, newEmail: string) {
  const user = currentUser();
  console.log('Requesting email change for user', user?.email, 'to', newEmail);
  if (user == null) {
    return Promise.reject(new Error('No user is signed in.'));
  }
  const credential = EmailAuthProvider.credential(user.email || '', currentPassword);
  const continueUrl = `${authContinueOrigin()}/email-verification?returnTo=/settings`;
  const actionCodeSettings = {
    url: continueUrl,
    handleCodeInApp: true
  };
  try {
    await reauthenticateWithCredential(user, credential);
    console.log('Re-authenticated user for email change');
    const requestChange = httpsCallable<{
      newEmail: string;
      locale?: string;
      continueUrl?: string;
    }, {
      provider: AuthEmailProvider;
    }>(functions, "auth-requestEmailChange");
    const {
      data
    } = await requestChange({
      newEmail: newEmail.trim(),
      locale: authEmailLocale(),
      continueUrl
    });
    if (data?.provider === "resend") {
      return;
    }
    await verifyBeforeUpdateEmail(user, newEmail, actionCodeSettings);
  } catch (error: unknown) {
    const err = error as {
      code?: string;
      message?: string;
    };
    console.log('Error in email change flow', err.code, err.message);
    const code = err.code || "";
    let message = err.message || "Unable to change email.";
    if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') {
      message = "The current password is incorrect.";
    } else if (code === 'auth/user-disabled') {
      message = "The user corresponding to the given email has been disabled.";
    } else if (code === 'auth/email-already-in-use' || code === 'already-exists' || code === 'functions/already-exists') {
      message = "The new email address is already in use by another account.";
    } else if (code === 'auth/invalid-email' || code === 'invalid-argument' || code === 'functions/invalid-argument') {
      message = "The new email address is not valid.";
    } else if (code === 'auth/requires-recent-login') {
      message = "This operation is sensitive and requires recent authentication. Please log in again.";
    }
    throw new Error(message);
  }
};

/**
 * Request a password reset email.
 *
 * `auth-requestPasswordReset` looks up the account (re-enabling enumeration for
 * clearer UX). When TRANSACTIONAL_EMAIL_PROVIDER=resend it sends via Resend;
 * otherwise the client falls back to Firebase `sendPasswordResetEmail`.
 */
export const resetPassword = async function (email: string) {
  const trimmedEmail = email.trim();
  const continueUrl = `${authContinueOrigin()}/email-verification`;
  const requestReset = httpsCallable<{
    email: string;
    locale?: string;
    continueUrl?: string;
  }, {
    exists: boolean;
    provider?: AuthEmailProvider;
  }>(functions, "auth-requestPasswordReset");
  try {
    const {
      data
    } = await requestReset({
      email: trimmedEmail,
      locale: authEmailLocale(),
      continueUrl
    });
    if (!data?.exists) {
      const notFoundError = new Error("No account associated with that email.") as Error & {
        code: string;
      };
      notFoundError.code = "auth/user-not-found";
      throw notFoundError;
    }
    if (data.provider === "resend") {
      return true;
    }
  } catch (error: unknown) {
    const code = (error as {
      code?: string;
    })?.code;
    if (code !== "auth/user-not-found") {
      console.log("Error requesting password reset", code, (error as {
        message?: string;
      })?.message);
    }
    throw error;
  }
  try {
    await sendPasswordResetEmail(auth, trimmedEmail, {
      url: continueUrl,
      handleCodeInApp: true
    });
    return true;
  } catch (error: unknown) {
    const code = (error as {
      code?: string;
    })?.code;
    const message = (error as {
      message?: string;
    })?.message;
    console.log("Error sending password reset email", code, message);
    throw error;
  }
};

/**
 * Logout a user. Use this a component and pass in the button or element you want to use to initiate logout
 * @param children
 * @constructor
 */
export const Logout = function Logout({
  children,
  onLogout,
  onError
}: {
  children: React.ReactNode;
  onLogout?: () => void | Promise<void>;
  onError?: (error: unknown) => void;
}) {
  const handleLogout = async () => {
    try {
      await signOut(auth);
      logAnalyticsEvent('logout');
      console.log("Signed out successfully");
      if (onLogout) {
        await onLogout();
      }
    } catch (error) {
      console.log("Error signing out");
      onError?.(error);
    }
  };
  return <span onClick={handleLogout}>
            {children}
        </span>;
};
export const getProfileInfo = function (user: User | null | undefined) {
  if (!user) {
    return {
      displayName: null,
      shortName: null,
      initals: null,
      email: null,
      photoURL: null,
      emailVerified: null,
      uid: null
    };
  }
  const shortName = user.displayName?.split(" ")[0];
  const initals = user.displayName?.split(" ").map(n => n[0]).join("");
  return {
    displayName: user.displayName,
    shortName,
    initals,
    email: user.email,
    photoURL: user.photoURL,
    emailVerified: user.emailVerified,
    uid: user.uid
  };
};
export function sendSignupVerificationEmail(email: string): Promise<boolean> {
  return sendVerificationEmail().then(() => {
    trackToltReferralSignup(email);
    return true;
  }).catch(verificationError => {
    console.error("Signup verification email failed", verificationError);
    return false;
  });
}
export async function sendVerificationEmail() {
  const user = currentUser();
  if (!user) {
    return Promise.reject("No user is signed in.");
  }
  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const storedClaim = readPublicVideoClaimContext();
  const passthrough = new URLSearchParams();
  const claim = params.get("claim") || storedClaim?.claimToken;
  const ref = params.get("ref") || storedClaim?.resolvedRefCode;
  const src = params.get("src") || storedClaim?.sourceContext;
  const vid = params.get("vid") || storedClaim?.sourceUserVideoId;
  const utmSource = params.get("utm_source");
  const utmCampaign = params.get("utm_campaign");
  const returnTo = getSafeReturnToFromLocation();
  if (claim) passthrough.set("claim", claim);
  if (ref) passthrough.set("ref", ref);
  if (src) passthrough.set("src", src);
  if (vid) passthrough.set("vid", vid);
  if (utmSource) passthrough.set("utm_source", utmSource);
  if (utmCampaign) passthrough.set("utm_campaign", utmCampaign);
  if (returnTo) {
    passthrough.set("returnTo", returnTo);
    persistReturnTo(returnTo);
  }
  const querySuffix = passthrough.toString();
  const verificationUrl = `${authContinueOrigin()}/email-verification${querySuffix ? `?${querySuffix}` : ""}`;
  const actionCodeSettings = {
    url: verificationUrl,
    handleCodeInApp: true
  };
  const requestVerify = httpsCallable<{
    locale?: string;
    continueUrl?: string;
  }, {
    provider: AuthEmailProvider;
  }>(functions, "auth-sendEmailVerification");
  try {
    const {
      data
    } = await requestVerify({
      locale: authEmailLocale(),
      continueUrl: verificationUrl
    });
    if (data?.provider === "resend") {
      return;
    }
    return await sendEmailVerification(user, actionCodeSettings);
  } catch (error) {
    const reason = readVerificationSendReason(error);
    if (reason && error && typeof error === "object") {
      try {
        Object.defineProperty(error, "reason", {
          value: reason,
          enumerable: true,
          configurable: true
        });
      } catch {
        // Frozen provider errors still throw; callers can read customData.details.
      }
    }
    throw error;
  }
}
export async function handleEmailVerification(auth: any, oobCode: string) {
  try {
    await applyActionCode(auth, oobCode);
    console.log("Email verified");
  } catch (error) {
    console.log("Error verifying email", error);
    throw error;
  }
}
export function checkEmailVerified(user: User | null | undefined, refresh: () => void) {
  console.log('User', user);
  if (user && !user.emailVerified) {
    return user.reload().then(() => {
      refresh && refresh();
      console.log('User after reload', user);
      if (user && user?.emailVerified) {
        console.log("Email verified, refreshing token");
        return user.getIdToken(true).then(() => {
          return true;
        });
      } else {
        console.log("Email not verified");
        return Promise.resolve(false);
      }
    });
  } else if (user && user?.emailVerified) {
    return Promise.resolve(true);
  } else {
    return Promise.resolve(false);
  }
}